import Header from 'components/JourneyViewer/Header';
import React, { useState, useRef, useEffect } from 'react';
import request from 'utils/request_ajax';
import { routes as ROUTES } from 'constants/config';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Icon } from 'ui';
import { CheckCircle, Calendar } from 'react-feather';
import { v4 as uuidv4 } from 'uuid';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useFeatureFlags, useSessionStorage } from 'hooks';
import classNames from 'classnames';
import JourneyBanner from './Banner/JourneyBanner';
import ContentList from './Content/ContentList';
import Sidebar from './Sidebar/Sidebar';
import TopbarBuilder from './Topbar/TopbarBuilder';
import EditDetails from './EditDetails';
import ContentOption from './Sidebar/ContentOption';
import ContentRenderer from './Content/ContentRender';
import * as actions from '../../actions/journeyBuilder';
import { displayBanner, updateBanner } from '../../actions/journeyBuilder';
import { formattedDateTime } from './__helpers__/journeyHelper';

const customCollisionDetectionAlgorithm = args => {
  // First, let's see if there are any collisions with the pointer
  const pointerCollisions = pointerWithin(args);

  // Collision detection algorithms return an array of collisions
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }

  // If there are no collisions with the pointer, return rectangle intersections
  return rectIntersection(args);
};

const BUILDER_ELEMENTS = [
  {
    id: 1,
    type: 'event',
    contentType: 'Event',
    icon: <Calendar className="JourneyBuilderContentOption-icon" />,
    text: t('journeys.element_types.event'),
  },
  {
    id: 2,
    type: 'lesson',
    contentType: 'Lesson',
    icon: <Icon className="JourneyBuilderContentOption-icon" name="lesson" />,
    text: t('journeys.element_types.lesson'),
  },
  {
    id: 3,
    type: 'path',
    contentType: 'Path',
    icon: <Icon className="JourneyBuilderContentOption-icon" name="path" />,
    text: t('journeys.element_types.path'),
  },
  {
    id: 4,
    type: 'task',
    contentType: 'Task',
    icon: <CheckCircle className="JourneyBuilderContentOption-icon" />,
    text: t('journeys.element_types.task'),
  },
];

const JourneyBuilder = props => {
  const [activeSidebarField, setActiveSidebarField] = useState(null);
  const [activeContentField, setActiveContentField] = useState(null);
  const isIframedInSeismic = useSessionStorage('iframedInSeismic');
  const hasUnificationFeatureFlag = useFeatureFlags(
    'readiness-enable-unification',
  );

  const { steps, journeyStatus } = useSelector(state => state);
  const {
    showEditDetails,
    previewing,
    showBanner,
    showDeleteConfirmationModal,
  } = useSelector(state => state.ui);

  const { journeyId } = useParams();

  const dispatch = useDispatch();

  const spacerInsertedRef = useRef();
  const [dragOverlayRef, setDragOverlayRef] = useState();

  const { message, status } = useSelector(state => state);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    pubsub.publish('journeyHideNav', true);

    return () => {
      pubsub.publish('journeyHideNav', false);
    };
  }, [previewing]);

  const handleDragStart = event => {
    const { active } = event;
    const activeData = active?.data?.current;

    if (activeData.fromSidebar) {
      setActiveSidebarField(activeData.option);

      return;
    }

    setActiveContentField(activeData);
    const spacer = {
      ...activeData.step,
      contentType: 'Spacer',
      originalContentType: activeData.step.contentType,
    };

    dispatch(
      actions.updateStep({
        index: activeData.idx,
        step: spacer,
      }),
    );

    spacerInsertedRef.current = true;
  };

  const handleDragOver = event => {
    const { over } = event;

    const overData = over?.data?.current;

    if (!spacerInsertedRef.current) {
      if (overData && (overData.step || overData.isContainer)) {
        let spacer;

        if (activeContentField) {
          spacer = {
            ...activeContentField.step,
            contentType: 'Spacer',
            originalContentType: activeContentField.step.contentType,
          };
        } else {
          spacer = {
            id: uuidv4(),
            contentType: 'Spacer',
          };
        }

        dispatch(
          actions.addStep({
            index: overData.idx,
            step: spacer,
          }),
        );

        spacerInsertedRef.current = true;
      }
    } else if (!over) {
      dispatch(
        actions.removeStep({
          index: steps.findIndex(x => x.contentType === 'Spacer'),
        }),
      );

      spacerInsertedRef.current = false;
    } else {
      const spacerIndex = steps.findIndex(x => x.contentType === 'Spacer');
      const nextIndex = overData.idx > -1 ? overData.idx : steps.length - 1;

      if (nextIndex === spacerIndex) return;

      dispatch(
        actions.reorderSteps(arrayMove(steps, spacerIndex, overData.idx)),
      );
    }
  };

  const getTargetStepId = spacerIndex => {
    let target = null;

    // step is not the last step in array
    if (spacerIndex + 1 < steps.length) {
      target = steps[spacerIndex + 1].stepId;
    }

    return target;
  };

  const showReorderError = () => {
    dispatch(
      updateBanner({
        message: t('journeys.builder.reorder_error'),
        status: 'error',
      }),
    );
    dispatch(
      displayBanner({
        bannerDismissible: false,
      }),
    );
  };

  const handleDragEnd = event => {
    const { over } = event;
    const spacerIndex = steps.findIndex(x => x.contentType === 'Spacer');

    if (activeContentField) {
      if (over) {
        const spacer = steps[spacerIndex];
        const target = getTargetStepId(spacerIndex);

        const step = {
          ...spacer,
          targetStepId: target,
          contentType: spacer.originalContentType,
        };

        delete step.originalContentType;

        if (step.stepId) {
          request
            .patchJSON(ROUTES.JOURNEY.STEPS.RESOURCE(journeyId, step.stepId), {
              target_step_id: target,
              journey_status: journeyStatus,
            })
            .then(responseData => {
              dispatch(
                actions.updateAutosavedAt({
                  autosavedAt: formattedDateTime(responseData.updatedAt),
                }),
              );
              dispatch(
                actions.updateJourneyStatus({
                  journeyStatus: responseData.journeyStatus,
                }),
              );
            })
            .fail(err => {
              console.error(err);
              showReorderError();
            });
        }

        if (step.stepId !== undefined) {
          dispatch(
            actions.updateStep({
              index: spacerIndex,
              step,
            }),
          );
        } else {
          showReorderError();
        }
      } else {
        dispatch(
          actions.addStep({
            index: activeContentField.idx,
            step: activeContentField.step,
          }),
        );
      }
    }

    if (activeSidebarField && over) {
      const newStep = {
        id: uuidv4(),
        stepId: null,
        targetStepId: getTargetStepId(spacerIndex),
        title: null,
        description: null,
        contentType: activeSidebarField.contentType,
        contentId: null,
        editing: false,
      };

      dispatch(
        actions.updateStep({
          index: spacerIndex,
          step: newStep,
        }),
      );
    }

    if (!over && spacerIndex > -1) {
      dispatch(
        actions.removeStep({
          index: spacerIndex,
        }),
      );
    }

    setTimeout(() => {
      setActiveSidebarField(null);
      setActiveContentField(null);

      spacerInsertedRef.current = false;
    }, 200);
  };

  const showDropAnimation =
    (activeSidebarField && !spacerInsertedRef.current) || activeContentField
      ? true
      : null;

  const builderClassName = classNames('JourneyBuilder JourneyBuilder-editing', {
    'JourneyBuilder-editing--showBanner': showBanner,
    'JourneyBuilder--iFrame': isIframedInSeismic && hasUnificationFeatureFlag,
    'JourneyBuilder--showModal': showDeleteConfirmationModal,
  });

  const dndDisabled = showBanner ? { pointerEvents: 'none' } : {};

  return (
    <div>
      {showBanner && <JourneyBanner message={message} state={status} />}
      <div className={builderClassName}>
        <TopbarBuilder />
        <DndContext
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          sensors={sensors}
          collisionDetection={customCollisionDetectionAlgorithm}
        >
          <div className="JourneyBuilder-side" style={dndDisabled}>
            <Sidebar elements={BUILDER_ELEMENTS} />
          </div>
          {showEditDetails && <EditDetails />}
          <div className="JourneyBuilder-main" style={dndDisabled}>
            <div className="JourneyBuilder-content">
              <Header />
              <ContentList spacerHeight={dragOverlayRef?.offsetHeight} />
            </div>
          </div>
          <DragOverlay
            dropAnimation={showDropAnimation}
            style={{ zIndex: 1005 }}
          >
            <div ref={newRef => setDragOverlayRef(newRef)}>
              {activeSidebarField ? (
                <ContentOption option={activeSidebarField} />
              ) : null}
              {activeContentField ? (
                <ContentRenderer
                  type={activeContentField.step.contentType}
                  step={activeContentField.step}
                  idx={activeContentField.idx}
                />
              ) : null}
            </div>
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
};

export default JourneyBuilder;
